import React from 'react';
import { MenuOutlined } from '@ant-design/icons';
import './MobileTabBar.css';

// The bar along the bottom of a phone: the few screens a person goes to all
// day, one thumb-press away, and the rest behind "More".
//
// It is another way to the same places and nothing else. The tabs are taken
// from the side menu's own list - the same keys, icons and labels, so what a
// role may see is decided in one place - and a press does exactly what
// pressing the menu entry does. "More" opens the side menu itself.

// Which screens earn a tab, in order of preference. A role gets the first
// four of these that are in its menu.
const PREFERRED = [
    'dashboard', 'appointments', 'patients', 'orders', 'prescriptions', 'tests',
    'branches', 'doctors', 'hospitals', 'dental', 'accounting', 'catalog',
    'appointments-report', 'reports', 'companysellers'
];

const MAX_TABS = 4;

const screenOf = (key) => String(key).split('/').pop();

// The menu entry a path belongs to: the entry itself, or the one a deeper
// page hangs under ("/doctor/patient/123" is under Patients).
export const tabFor = (items, pathname) => {
    const exact = items.find((item) => item.key === pathname);
    if (exact) return exact.key;

    // "/doctor/patient/123": the screen is "patient", and the entry it hangs
    // under is the one named for it in the plural.
    const screen = String(pathname).split('/')[2];
    if (!screen) return null;
    const under = items.find((item) => {
        const name = screenOf(item.key);
        return name !== screen && name.startsWith(screen) && name.length - screen.length <= 2;
    });
    return under ? under.key : null;
};

export const pickTabs = (items) => {
    const byScreen = new Map(items.map((item) => [screenOf(item.key), item]));
    const preferred = PREFERRED.map((screen) => byScreen.get(screen)).filter(Boolean);
    // A role whose menu has none of the usual screens still gets its first few.
    const rest = items.filter((item) => !preferred.includes(item));
    return [...preferred, ...rest].slice(0, MAX_TABS);
};

const MobileTabBar = ({ items = [], pathname, onNavigate, onMore, moreOpen = false, moreLabel = 'More' }) => {
    const tabs = pickTabs(items);
    // With a menu this short the bar already holds all of it.
    const hasMore = items.length > tabs.length;
    const current = tabFor(items, pathname);

    if (tabs.length === 0) return null;

    return (
        <nav className="mobile-tabbar" aria-label="Main">
            {tabs.map((item) => {
                const active = !moreOpen && item.key === current;
                return (
                    <button
                        type="button"
                        key={item.key}
                        className={`mobile-tab ${active ? 'mobile-tab-active' : ''}`}
                        aria-current={active ? 'page' : undefined}
                        onClick={() => onNavigate(item.key)}
                    >
                        <span className="mobile-tab-icon">{item.icon}</span>
                        <span className="mobile-tab-label">{item.label}</span>
                    </button>
                );
            })}
            {hasMore && (
                <button
                    type="button"
                    className={`mobile-tab ${moreOpen || (current && !tabs.some((item) => item.key === current)) ? 'mobile-tab-active' : ''}`}
                    aria-expanded={moreOpen}
                    onClick={onMore}
                >
                    <span className="mobile-tab-icon"><MenuOutlined /></span>
                    <span className="mobile-tab-label">{moreLabel}</span>
                </button>
            )}
        </nav>
    );
};

export default MobileTabBar;
